import React, { useState, useEffect } from 'react';
import { 
  Share2, Copy, Check, Users, Award, TrendingUp, Crown, 
  ExternalLink, Clock, UserPlus, Gift
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function ReferralCard({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [claimingReward, setClaimingReward] = useState(false);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/referral/my-stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.warn('Failed to fetch referral stats:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimReward = async () => {
    setClaimingReward(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/referral/claim-on-call-reward`, {}, { withCredentials: true });
      if (response.data.success) {
        toast.success(response.data.message || 'Reward claimed successfully!');
        fetchReferralStats();
      } else {
        toast.info(response.data.message || 'No rewards to claim right now');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim reward');
    } finally {
      setClaimingReward(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share && stats?.referral_link) {
      try {
        await navigator.share({
          title: 'Join OnsiteHand',
          text: 'Join me on OnsiteHand - the best platform for connecting tradespeople and employers!',
          url: stats.referral_link
        });
      } catch { copyToClipboard(stats.referral_link); }
    } else if (stats?.referral_link) {
      copyToClipboard(stats.referral_link);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '';

  if (loading) {
    return (
      <div className="card animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-4" />
        <div className="h-20 bg-slate-100 rounded mb-4" />
        <div className="h-10 bg-slate-200 rounded" />
      </div>
    );
  }

  if (!stats) return null;

  const isTradesPerson = user?.user_type === 'tradesperson';
  const sb = stats.status_breakdown || {};

  return (
    <div className="card overflow-hidden" data-testid="referral-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <UserPlus className="h-5 w-5 text-orange-500" />
          <h3 className="text-lg font-bold text-slate-900">Referral Program</h3>
        </div>
        {stats.grandfather_status && (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 text-sm font-medium" data-testid="grandfather-badge">
            <Crown className="h-4 w-4" /> {stats.grandfather_label}
          </span>
        )}
      </div>

      {/* Referral Code & Link */}
      {stats.referral_code && (
        <div className="bg-slate-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Your Referral Code</span>
            <button onClick={() => copyToClipboard(stats.referral_code)} className="text-orange-500 hover:text-orange-600 flex items-center gap-1 text-sm">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy
            </button>
          </div>
          <code className="text-lg font-mono font-bold text-slate-900 bg-white px-3 py-2 rounded block text-center" data-testid="referral-code">
            {stats.referral_code}
          </code>
          {stats.referral_link && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">Share Link</span>
                <button onClick={handleShare} className="text-orange-500 hover:text-orange-600 flex items-center gap-1 text-sm">
                  <Share2 className="h-4 w-4" /> Share
                </button>
              </div>
              <div className="flex items-center gap-2">
                <input type="text" value={stats.referral_link} readOnly className="flex-1 text-sm bg-white rounded px-3 py-2 text-slate-600 truncate" onClick={(e) => e.target.select()} />
                <button onClick={() => copyToClipboard(stats.referral_link)} className="btn-secondary text-sm px-3 py-2">
                  <ExternalLink className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status Breakdown */}
      <div className="grid grid-cols-4 gap-2 mb-4" data-testid="referral-status-breakdown">
        <div className="text-center p-2 bg-yellow-50 rounded-lg">
          <p className="text-lg font-bold text-yellow-600">{sb.pending || 0}</p>
          <p className="text-[10px] text-slate-600">Pending</p>
        </div>
        <div className="text-center p-2 bg-blue-50 rounded-lg">
          <p className="text-lg font-bold text-blue-600">{sb.qualified || 0}</p>
          <p className="text-[10px] text-slate-600">Qualified</p>
        </div>
        <div className="text-center p-2 bg-green-50 rounded-lg">
          <p className="text-lg font-bold text-green-600">{sb.rewarded || 0}</p>
          <p className="text-[10px] text-slate-600">Rewarded</p>
        </div>
        <div className="text-center p-2 bg-red-50 rounded-lg">
          <p className="text-lg font-bold text-red-600">{sb.rejected || 0}</p>
          <p className="text-[10px] text-slate-600">Rejected</p>
        </div>
      </div>

      {/* Tradesperson: Priority Boost Reward */}
      {isTradesPerson && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 mb-4" data-testid="referral-reward-trades">
          <div className="flex items-center gap-2 mb-2">
            <Award className="h-5 w-5 text-orange-600" />
            <span className="font-bold text-slate-900">Earn Priority Boost</span>
          </div>
          <p className="text-sm text-orange-700 mb-2">
            Refer another trade and get a <strong>Priority Boost</strong> when they successfully join. Priority Boost helps your profile appear more prominently in employer searches for a limited time.
          </p>
          {stats.rewards_earned && stats.rewards_earned.length > 0 && (
            <div className="bg-white/60 rounded-lg p-3 mt-2">
              <p className="text-xs font-medium text-slate-700 mb-1 flex items-center gap-1"><Gift className="h-3 w-3" /> Boosts Earned</p>
              {stats.rewards_earned.map((rw) => {
                const daysLeft = rw.expires_at ? Math.max(0, Math.ceil((new Date(rw.expires_at) - new Date()) / 86400000)) : 0;
                return (
                  <div key={rw.reward_id} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                    <span className="text-slate-600">{rw.referrals_claimed} referral{rw.referrals_claimed !== 1 ? 's' : ''} claimed</span>
                    <span className={`font-medium ${daysLeft > 0 ? 'text-orange-600' : 'text-slate-400'}`}>
                      {daysLeft > 0 ? `Priority Boost Active — ${daysLeft} days left` : 'Expired'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {stats.unclaimed_referrals > 0 && (
            <button
              onClick={handleClaimReward}
              disabled={claimingReward}
              className="mt-3 w-full py-2 px-4 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="claim-referral-reward-btn"
            >
              {claimingReward ? (
                <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> Claiming...</>
              ) : (
                <><Gift className="h-4 w-4" /> Claim {stats.unclaimed_referrals} Priority Boost{stats.unclaimed_referrals !== 1 ? 'es' : ''}</>
              )}
            </button>
          )}
          {stats.unclaimed_referrals === 0 && sb.qualified > 0 && (
            <p className="mt-2 text-xs text-orange-600 font-medium">All Priority Boost rewards claimed. Refer more to earn more!</p>
          )}
          <p className="mt-2 text-[10px] text-slate-500 italic">Priority Boost does not guarantee work. Employers still choose who to contact.</p>
        </div>
      )}

      {/* Employer: Invite copy (free marketplace — no financial reward) */}
      {!isTradesPerson && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4" data-testid="employer-invite-copy">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="h-5 w-5 text-slate-700" />
            <span className="font-bold text-slate-900">Invite employers to OnSiteHand</span>
          </div>
          <p className="text-sm text-slate-600">
            Share your link with other employers so they can find verified tradespeople on OnSiteHand. It's free to use.
          </p>
        </div>
      )}

      {/* Recent Referrals with dates and status */}
      {stats.referrals && stats.referrals.length > 0 && (
        <div data-testid="referral-history">
          <h4 className="text-sm font-medium text-slate-700 mb-2">Referral History</h4>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {stats.referrals.slice(0, 10).map((ref) => (
              <div key={ref.referral_id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    ref.status === 'rewarded' ? 'bg-green-500' :
                    ref.status === 'qualified' ? 'bg-blue-500' :
                    ref.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                  }`} />
                  <span className="text-slate-700 truncate">{ref.invited_name || 'User'}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    ref.status === 'rewarded' ? 'bg-green-100 text-green-700' :
                    ref.status === 'qualified' ? 'bg-blue-100 text-blue-700' :
                    ref.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                  }`}>{ref.status}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${ref.invited_type === 'tradesperson' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                    {ref.invited_type}
                  </span>
                  <span className="text-[10px] text-slate-400">{fmtDate(ref.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        <button onClick={handleShare} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="share-referral-btn">
          <Share2 className="h-4 w-4" /> {isTradesPerson ? 'Invite a trade' : 'Invite employers to OnSiteHand'}
        </button>
      </div>
    </div>
  );
}
